<template>
  <fullLayout>
    <div
      class="pri-page"
      v-if="inited"
    >
      <div class="pri-wrapper">
        <transition name="left-slide">
          <div
            class="left-slide-outer"
            v-if="leftOpen"
          >
            <div class="left-wrapper">
              <leftLayout
                :pageConfig="pageConfig"
                v-model:open="leftOpen"
                :eventBus="eventBus"
                v-model:tableMode="tableMode"
              ></leftLayout>
            </div>
          </div>
        </transition>
        <div
          class="right-wrapper"
          ref="rightWrapperRef"
        >
          <div
            :class="['left-space', { 'setting-open': settingOpen }]"
            :style="{
              '--x': `${right_wrapper_left}px`,
              '--drawer_width': `${setting_drawer_width}px`,
            }"
          ></div>
          <rightTable
            :pageConfig="pageConfig"
            :modalType="field.modalType"
            ref="rightTableRef"
            :eventBus="eventBus"
            v-model:leftOpen="leftOpen"
            :setting_drawer_width="setting_drawer_width"
            :tableMode="tableMode"
            @changeMode="tableMode = $event"
          ></rightTable>
          <div
            :class="['right-space', { 'detail-open': detailState.open }]"
            :style="{
              '--detail_width': `${detailState.width}px`,
            }"
          ></div>
        </div>
      </div>
    </div>
  </fullLayout>
  <a-drawer
    :title="detailState.title"
    :width="detailState.width"
    placement="right"
    v-model:open="detailState.open"
    :mask="false"
    :push="false"
    v-if="detailState.open"
  >
    <component
      :is="detailState.component"
      :cmpObj="detailState.cmpObj"
      :extra="detailState.extra"
    ></component>
  </a-drawer>
</template>
<script setup lang="ts">
// ============================================================================
// sql_db_view 页面壳（v15 路径 / v20 配置裁剪 / v22 app_flag 常量 / v24 无按钮权限校验）
// 与 remotePages/v2/index.vue 的差异：
//   ① app_flag 固化为常量 'db_view'，不从路由解析、不向子组件传递（v22）；
//   ② 配置获取走本目录 pageConfig.ts 的 getDbViewConfig（get_page_config，
//      入参 { org_id, flag }）+ getSqlDbViewPageConfig（白名单融合），不复用
//      getDefaultAndServerConfig / getPageConfig（v10：那套会打到 lybase 通用页）；
//   ③ 只支持高级搜索（v20）；
//   ④ 无按钮权限校验（v24 决策 24）：不 import CHECK_AUTH、无权限位。
// ============================================================================
import fullLayout from 'liyu-pc-base/components/fullLayout/fullLayout.vue';
import {
  onMounted,
  ref,
  reactive,
  provide,
  computed,
  onUnmounted,
  onActivated,
  onDeactivated,
  nextTick,
  watch,
} from 'vue';
import leftLayout from './leftLayout.vue';
import rightTable from './rightTable.vue';
import mitt from 'mitt';
import { useRoute } from 'vue-router';
import { event_obj } from 'liyu-pc-base/init-plugin';
import { useUserStore, useRequiredConditionStore, useInjectMenuState } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { getSqlDbViewPageConfig } from './pageConfig';
import type { SqlViewPageConfig } from './typing';

const leftOpen = ref(true);
const inited = ref(false);
const rightTableRef = ref(null);
const eventBus = mitt();
const tableMode = ref<'normal' | 'pivot' | 'pivot_charts'>('normal');
const userStore = useUserStore();
const pageConfig = ref<SqlViewPageConfig | null>(null);
const { t } = useI18n();
const setting_drawer_width = ref(1000);
const rightWrapperRef = ref(null);
const right_wrapper_left = ref(0);
const injectMenuState = useInjectMenuState();
const { collapsed } = injectMenuState();
const detailState = reactive({
  open: false,
  component: null,
  width: 0,
  title: '详情',
  cmpObj: null,
  extra: null,
  lastLeftOpen: true,
  lastMenuCollapsed: true,
});

const calcRightWrapperLeft = () => {
  if (rightWrapperRef.value) {
    const rect = (rightWrapperRef.value as HTMLElement).getBoundingClientRect();
    right_wrapper_left.value = rect.left;
  }
};

const route = useRoute();
const requiredConditionStore = useRequiredConditionStore();
// 路由形态：/db_view/db_view-{flag}（§4.3）。modalType = 末段；view_flag = modalType 第二段。
// app_flag 是 URL 第一段且固定为 'db_view'，不再解析、不再传递（v22）。
const field = {
  modalType: route.path.split('/').pop() || '',
  modalName: '',
};
// view_flag（= modalType 第二段）由 getSqlDbViewPageConfig 内部解析，页面壳不再单独持有

const tableProState = computed(() => {
  return rightTableRef.value?.activeTableRef;
});
const settingOpen = computed(() => {
  return tableProState.value?.settingOpen;
});

provide('tableProStateProxy', tableProState);

const init = async () => {
  // v26：取数 + 白名单融合合一为 async getSqlDbViewPageConfig（内部两路并行取数）
  pageConfig.value = await getSqlDbViewPageConfig(field.modalType, userStore.currentOrg.id, t, {
    to_i18n: true,
  });
  if (!pageConfig.value?.isView) {
    leftOpen.value = false;
  } else if (pageConfig.value?.default_hide_view) {
    leftOpen.value = false;
  } else {
    leftOpen.value = true;
  }
  if (pageConfig.value?.required_condition) {
    requiredConditionStore.set_required_condition(
      field.modalType,
      pageConfig.value.required_condition,
    );
  }
  // URL hash 恢复初始表格模式（#pivot:uid / #pivotcharts:uid / #uid）
  const hash = window.location.hash.substring(1);
  let tmpModel = undefined;
  if (hash) {
    if (hash.startsWith('pivotcharts:')) {
      tmpModel = 'pivot_charts';
    } else if (hash.startsWith('pivot:')) {
      tmpModel = 'pivot';
    } else {
      tmpModel = 'normal';
    }
  }
  if (!pageConfig.value?.enable_view_types?.length) {
    tmpModel = undefined;
  } else {
    tmpModel = tmpModel || pageConfig.value?.default_view_type || 'normal';
    const isFind = pageConfig.value?.enable_view_types?.includes(tmpModel);
    if (!isFind) {
      tmpModel = pageConfig.value?.enable_view_types[0];
    }
  }
  tableMode.value = tmpModel;
};

const event_obj_reinit = () => {
  init();
  tableProState.value?.init();
};
const event_obj_refresh = () => {
  tableProState.value?.refresh();
};
const handleShowDetail = ({ key, cmpObj }) => {
  const detailConfigItem = pageConfig?.value?.detailConfigs?.[key];
  if (detailConfigItem) {
    detailState.component = detailConfigItem.component;
    detailState.width = detailConfigItem.width || 500;
    detailState.title = detailConfigItem.title || '详情';
    detailState.cmpObj = cmpObj;
    // extra：详情组件的私有配置（配置期常量），原样透传给详情组件
    detailState.extra = detailConfigItem.extra ?? null;
    if (!detailState.open) {
      detailState.open = true;
      detailState.lastLeftOpen = leftOpen.value;
      detailState.lastMenuCollapsed = collapsed.value;
      leftOpen.value = false;
      collapsed.value = true;
    }
  } else {
    console.warn(`detailConfigItem not found, key: ${key}`);
  }
};

const event_obj_local_refresh = () => {
  eventBus.emit('local_refresh');
};
onMounted(async () => {
  event_obj()?.on('refresh', event_obj_refresh);
  event_obj()?.on('reset_and_reload', event_obj_reinit);
  event_obj()?.on('local_refresh', event_obj_local_refresh);
  // ★ show_detail 走页面实例总线，避免嵌套详情串扰；
  //   保留全局监听作为兼容入口（旧版 lib / 外部仍可能向全局单例发 show_detail）
  eventBus.on('show_detail', handleShowDetail);
  event_obj()?.on('show_detail', handleShowDetail);
  await init();
  inited.value = true;
  nextTick(() => {
    calcRightWrapperLeft();
  });
});
onUnmounted(() => {
  event_obj()?.off('refresh', event_obj_refresh);
  event_obj()?.off('reset_and_reload', event_obj_reinit);
  event_obj()?.off('local_refresh', event_obj_local_refresh);
  eventBus.off('show_detail', handleShowDetail);
  event_obj()?.off('show_detail', handleShowDetail);
});
onActivated(() => {
  if (pageConfig.value?.required_condition) {
    requiredConditionStore.set_required_condition(
      field.modalType,
      pageConfig.value.required_condition,
    );
  }
  event_obj()?.off('refresh', event_obj_refresh);
  event_obj()?.off('reset_and_reload', event_obj_reinit);
  event_obj()?.off('local_refresh', event_obj_local_refresh);
  eventBus.off('show_detail', handleShowDetail);
  event_obj()?.off('show_detail', handleShowDetail);

  event_obj()?.on('refresh', event_obj_refresh);
  event_obj()?.on('reset_and_reload', event_obj_reinit);
  event_obj()?.on('local_refresh', event_obj_local_refresh);
  eventBus.on('show_detail', handleShowDetail);
  event_obj()?.on('show_detail', handleShowDetail);
});
onDeactivated(() => {
  event_obj()?.off('refresh', event_obj_refresh);
  event_obj()?.off('reset_and_reload', event_obj_reinit);
  event_obj()?.off('local_refresh', event_obj_local_refresh);
  eventBus.off('show_detail', handleShowDetail);
  event_obj()?.off('show_detail', handleShowDetail);
});
watch(
  () => detailState.open,
  (newVal, oldVal) => {
    if (newVal != oldVal && !newVal) {
      leftOpen.value = detailState.lastLeftOpen;
      collapsed.value = detailState.lastMenuCollapsed;
    }
  },
);
</script>
<style scoped lang="less">
.pri-page {
  height: 100%;
  padding-left: 4px;
  padding-right: 4px;
  padding-top: 4px;
}
.pri-wrapper {
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  gap: 4px;
}
.left-slide-outer {
  height: 100%;
  overflow: hidden;
  width: 220px;
  background-color: white;
}
.left-wrapper {
  height: 100%;
  overflow: hidden;
  width: 220px;
  background-color: white;
  padding-left: 4px;
  padding-right: 4px;
}
.left-slide-enter-active {
  transition: width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.left-slide-leave-active {
  transition: width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.left-slide-enter-from,
.left-slide-leave-to {
  width: 0;
  opacity: 0;
}
.left-slide-leave-from,
.left-slide-enter-to {
  width: 220px;
  opacity: 1;
}
.right-wrapper {
  display: flex;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background-color: white;
  flex-direction: row;
  justify-content: space-between;
}
.left-space {
  width: 0;
  transition: width 0.2s ease;
  &.setting-open {
    width: calc(var(--drawer_width) - var(--x));
  }
}
.right-space {
  width: 0;
  transition: width 0.2s ease;
  &.detail-open {
    width: calc(var(--detail_width));
  }
}
</style>
